import { unstable_ClassNameGenerator as ClassNameGenerator } from '@mui/material/className';

ClassNameGenerator.configure((componentName: string) => componentName.replace('Mui', 'V5')); // load as early as possible this code in a js file
import '../../utils/MuiClassNameSetup';
import { tolgeeModule } from '@rakutenfrance/tolgee-provider';

import React from 'react';

import headerModule from '@rakutenfrance/header';
import { configService as configurationService, publicRuntimeConfig } from '@rakutenfrance/config-service';

import { getServerSidePropsLoader, regroupModuleReducers, GetServerSidePropsReturn } from '@rakutenfrance/redux-apollo-helper';
import { renderReact } from '../../fragmentHelper/renderReactCustom';

import { ReduxApolloFragmentWrapperChildren } from '../../fragmentHelper/Wrapper';

import { createQuery } from './PageQuery';
import { GetServerSidePropsHypernovaWatchContext, PagePropsType } from '../../types';

import '@rakutenfrance/header/build/esm/style.global.css';

export { headerModule };

const config = configurationService.get();
const shouldDebugPreheaderEncoding = process.env.DEBUG_HEADER_PREHEADER_ENCODING === 'true';

const { HeaderContainer } = headerModule.containers;

const graphQlQuery = createQuery(
  {
    headerModule,
  },
  {
    // includePersonalQuery: config.featureFlipping.ssrPersonalize,
    // includePersonalQuery: typeof window !== 'undefined',
    includePersonalQuery: config.featureFlipping.ssrPersonalize || typeof window !== 'undefined',
    // includePersonalQuery: true,
  }
);

const modules = [headerModule, tolgeeModule];
const reducers = regroupModuleReducers(modules);

const getPreheaderTextsFromState = (initialReduxState: any): string[] => {
  const preHeaders = initialReduxState?.header?.marketing?.preHeader;
  if (!Array.isArray(preHeaders) || preHeaders.length === 0) {
    return [];
  }

  return preHeaders.flatMap((item: any) => {
    const desktopText = item?.creation?.text_desktop;
    const mobileText = item?.creation?.text_mobile;
    return [desktopText, mobileText].filter((text): text is string => typeof text === 'string' && text.length > 0);
  });
};

const logPreheaderEncodingDiagnostics = (initialReduxState: any): void => {
  if (!shouldDebugPreheaderEncoding) {
    return;
  }

  const texts = getPreheaderTextsFromState(initialReduxState);
  if (texts.length === 0) {
    console.log('[Header][encoding] no preheader text found in redux state');
    return;
  }
};

const serializeHeaderWithSafeEuroEncoding = (serialized: string): string => {
  if (!serialized.includes('€')) {
    return serialized;
  }

  const scriptTagStart = '<script type="application/json"';
  const scriptIndex = serialized.indexOf(scriptTagStart);

  if (scriptIndex < 0) {
    return serialized.replace(/€/g, '&euro;');
  }

  const htmlFragment = serialized.slice(0, scriptIndex).replace(/€/g, '&euro;');
  const hydrationPayload = serialized.slice(scriptIndex).replace(/€/g, '\\u20AC');

  return `${htmlFragment}${hydrationPayload}`;
};

const WrappedHeader: React.FC<PagePropsType> = ({ initialApolloState, initialReduxState }) => {
  const graphQlvariables = {};

  return (
    <>
      <script
        /* add the server side config loaded from env vars to be usable in the browser, this config is used by  */
        dangerouslySetInnerHTML={{
          __html: `
        window.PUBLIC_RUNTIME_CONFIG = ${JSON.stringify(publicRuntimeConfig)};
      `,
        }}
      />
      <ReduxApolloFragmentWrapperChildren initialApolloState={initialApolloState} initialReduxState={initialReduxState} reducers={reducers}>
        <HeaderContainer PERSONALIZED_QUERY_OPTION={graphQlvariables} PERSONALIZED_QUERY={graphQlQuery} isHypernova />
      </ReduxApolloFragmentWrapperChildren>
    </>
  );
};

const baseRenderFunction = renderReact('Header', WrappedHeader);
const wrappedRenderFunction = ((...args: Parameters<typeof baseRenderFunction>) => {
  const serialized = baseRenderFunction(...args);
  if (typeof serialized !== 'string') {
    return serialized;
  }

  return serializeHeaderWithSafeEuroEncoding(serialized);
}) as typeof baseRenderFunction;

Object.assign(wrappedRenderFunction, baseRenderFunction);

export const renderFunction = wrappedRenderFunction;

export const serverCanCache = !config.featureFlipping.ssrPersonalize;

export const getServerSideProps = async (watchContext: GetServerSidePropsHypernovaWatchContext): Promise<GetServerSidePropsReturn> => {
  const baseUrl = '';
  const preloadedConfig = {
    header: {
      isHypernova: true,
    },
  };
  const graphQlVariables = {};

  const { initialReduxState, initialApolloState } = await getServerSidePropsLoader(
    watchContext,
    modules,
    baseUrl,
    preloadedConfig,
    graphQlQuery,
    graphQlVariables
  );

  logPreheaderEncodingDiagnostics(initialReduxState);

  return {
    props: {
      initialReduxState,
      initialApolloState,
    },
  };
};
